import { useEffect, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { AuthenticatedRoutes } from '@/router/routes';

import { mapInvoiceDetailsToUpdateFormValues, mapUpdateInvoiceFormToRequest } from '../mappers';
import { InvoiceAllowedAction, InvoiceDetails } from '../types';
import { UpdateInvoiceFormValues, updateInvoiceValidationSchema } from '../validations';
import { useInvoiceDetails } from './use-invoice-details';
import { useUpdateInvoice } from './use-update-invoice';

interface InvoiceDetailsLocationState {
  invoicesSearch?: string;
}

interface UnsavedChangesController {
  isModalOpen: boolean;
  handleModalDiscard: () => void;
  handleModalCancel: () => void;
}

export interface InvoiceDetailsPageController {
  details?: InvoiceDetails;
  form: UseFormReturn<UpdateInvoiceFormValues>;
  isLoading: boolean;
  isError: boolean;
  isReadOnly: boolean;
  isUpdating: boolean;
  navigateBack: () => void;
  cancelEdit: () => void;
  submit: () => Promise<void>;
  unsavedChanges: UnsavedChangesController;
}

const EMPTY_FORM_VALUES: UpdateInvoiceFormValues = {
  period: { from: undefined, to: undefined },
  issuedDate: undefined,
  paymentTerm: '',
  currencyCode: '',
  paymentDetailsId: null,
  receiverEmails: { to: '', cc: '', bcc: '' },
  items: [],
};

/** Owns route parsing, edit state, form hydration, mutation, and navigation. */
export const useInvoiceDetailsPage = (): InvoiceDetailsPageController => {
  const navigate = useNavigate();
  const location = useLocation();
  const { invoiceId: invoiceIdParam } = useParams<{ invoiceId: string }>();
  const hydratedInvoiceIdRef = useRef<number | null>(null);
  const parsedInvoiceId = Number(invoiceIdParam);
  const invoiceId =
    Number.isInteger(parsedInvoiceId) && parsedInvoiceId > 0 ? parsedInvoiceId : undefined;
  const query = useInvoiceDetails(invoiceId);
  const updateMutation = useUpdateInvoice();
  const locationState = (location.state as InvoiceDetailsLocationState | null) ?? null;

  const form = useForm<UpdateInvoiceFormValues>({
    resolver: zodResolver(updateInvoiceValidationSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: EMPTY_FORM_VALUES,
  });

  const isReadOnly = !query.data?.allowedActions.includes(InvoiceAllowedAction.Edit);

  useEffect(() => {
    if (!query.data) return;

    const shouldHydrate = hydratedInvoiceIdRef.current !== query.data.id || !form.formState.isDirty;

    if (shouldHydrate) {
      form.reset(mapInvoiceDetailsToUpdateFormValues(query.data));
      // reset does not run the resolver, so validate the loaded snapshot before Save is gated by isValid.
      if (!isReadOnly) void form.trigger();
      hydratedInvoiceIdRef.current = query.data.id;
    }
  }, [form, isReadOnly, query.data]);

  const cancelEditing = () => {
    if (query.data) {
      form.reset(mapInvoiceDetailsToUpdateFormValues(query.data));
    }
  };

  const unsavedChangesGuard = useUnsavedChangesGuard({
    isDirty: !isReadOnly && form.formState.isDirty,
    onDiscard: cancelEditing,
  });

  const navigateToInvoices = () => {
    navigate(
      { pathname: AuthenticatedRoutes.Invoices, search: locationState?.invoicesSearch ?? '' },
      { replace: true, state: null }
    );
  };

  const navigateBackAction = () => {
    if (locationState?.invoicesSearch !== undefined || location.key === 'default') {
      navigateToInvoices();
      return;
    }

    navigate(-1);
  };

  const navigateBack = () => {
    if (unsavedChangesGuard.checkUnsavedChanges(navigateBackAction)) {
      navigateBackAction();
    }
  };

  const cancelEdit = () => {
    if (unsavedChangesGuard.checkUnsavedChanges(cancelEditing)) {
      cancelEditing();
    }
  };

  const submit = form.handleSubmit(values => {
    if (!invoiceId || !query.data) return;

    updateMutation.mutate(
      {
        invoiceId,
        request: mapUpdateInvoiceFormToRequest({ details: query.data, values }),
      },
      {
        onSuccess: () => {
          form.reset(values);
          unsavedChangesGuard.allowNavigation();
          navigateToInvoices();
        },
      }
    );
  });

  return {
    details: query.data,
    form,
    isLoading: query.isLoading,
    isError: query.isError,
    isReadOnly,
    isUpdating: updateMutation.isPending,
    navigateBack,
    cancelEdit,
    submit,
    unsavedChanges: {
      isModalOpen: unsavedChangesGuard.isModalOpen,
      handleModalDiscard: unsavedChangesGuard.handleModalDiscard,
      handleModalCancel: unsavedChangesGuard.handleModalCancel,
    },
  };
};
